import { Paper, Box } from "@mui/material";
import Sidebar from "../../sidebar/Sidebar";
import MobileSidebar from "../../mobileSidebar/MobileSidebar";
import useResponsiveInfo from "../../../hooks/useResponsiveInfo";
import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch } from "../../..";
import * as loginActions from '../../../store/actions/login';
import { useSelector } from "react-redux";
import jwtDecode from "jwt-decode";
import { UserService } from "../../../core/http/service/UserService";

interface IContainerWithSidebar {
  children: any;
}

const ContainerWithSidebar = ({ children }: IContainerWithSidebar) => {
  const { isMobile, isTablet, isDesktop } = useResponsiveInfo();
  const { token } = useSelector((state: any) => state.login);
  const [isMobileSidebarToggled, setIsMobileSidebarToggled] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("spm_sidebar_collapsed") === "true";
  });

  const rawEnv = (process.env.REACT_APP_ENV || (process.env.NODE_ENV === "development" ? "DEV" : "")).toUpperCase();
  const hasEnvBanner = rawEnv && rawEnv !== "PRODUCTION";

  const dispatch = useAppDispatch();
  const currentLocation = useLocation();

  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("spm_sidebar_collapsed", String(next));
      return next;
    });
  }, []);

  const toggleMobileSidebar = useCallback(() => {
    setIsMobileSidebarToggled((prev) => !prev);
  }, []);

  // Mostrar o alert se o usuário quer sair da página.
  useEffect(() => {
    window.onbeforeunload = function () {
      return "Bloqueio de sair da página";
    };
  }, []);

  const testRefreshToken = useCallback(async () => {
    try {
      const service: UserService = new UserService();
      await service.getInfo();
    } catch (error: any) {
      if (error.response?.data?.errors?.[0] === 'RefreshToken inválido') {
        alert('Sessão expirada, faça login novamente!');
        await dispatch(loginActions.logout());
      }
    }
  }, [dispatch]);

  useEffect(() => {
    if (token !== null) {
      const decoded_token: any = jwtDecode(token);
      const expDate = new Date(decoded_token.exp * 1000);
      const now = new Date();
      if (now > expDate) {
        testRefreshToken();
      }
    }
  }, [token, testRefreshToken]);

  const isDesktopOrientation = isDesktop && !isTablet && !isMobile;
  const sidebarWidth = isCollapsed ? "74px" : "280px";

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", width: "100%", overflowX: "hidden" }}>
      {isDesktopOrientation ? (
        <>
          <Sidebar
            currentRoute={currentLocation.pathname}
            isCollapsed={isCollapsed}
            toggleCollapse={toggleCollapse}
          />
          <Box
            component={Paper}
            elevation={6}
            square
            sx={{
              flexGrow: 1,
              marginLeft: sidebarWidth,
              width: `calc(100% - ${sidebarWidth})`,
              minHeight: "100vh",
              transition: "margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              backgroundColor: "white",
              px: { md: 4, lg: 5 },
              py: hasEnvBanner ? 5 : 4,
              boxSizing: "border-box",
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                backgroundColor: "white",
                flexDirection: "column",
                width: "100%",
              }}
            >
              {children}
            </Box>
          </Box>
        </>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%", minHeight: "100vh" }}>
          <MobileSidebar
            isMobileSidebarToggled={isMobileSidebarToggled}
            toggleMobileSidebar={toggleMobileSidebar}
            currentRoute={currentLocation.pathname}
          />
          <Box
            component={Paper}
            elevation={0}
            square
            sx={{
              flexGrow: 1,
              backgroundColor: "white",
              px: { xs: 2, sm: 4 },
              py: hasEnvBanner ? 5 : 3,
              boxSizing: "border-box",
            }}
          >
            {children}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ContainerWithSidebar;
